import type { UseQueryResult } from "react-query";
import { useEffect } from "react";
import memoize from "memoizee";
import jsonStableStringify from "json-stable-stringify";
import takeRight from "lodash/takeRight";
import mapValues from "lodash/mapValues";
import * as LaunchDarkly from "launchdarkly-js-client-sdk";

import {
  FetcherQueryKey,
  QueryFunction,
  computeIsQueryEnabled as computeIsQueryEnabledFromFetcherParams,
  registerOnQueryRemoveCallback,
} from "../../../services/query";
import type {
  FeatureFlagsAppName as AppName,
  FeatureFlagsDataEnvelope,
  FeatureFlagsNetworkResponse,
} from "../../../models/featureFlags";
import { GranularRuntimeEnvName } from "../../../helpers/url";

export const LAUNCH_DARKLY_KEYS = {
  fabric: {
    granappdevelopment: "6078a0cb4f67d50c1f00be16",
    granapptest: "6079c2e21a20810be0f7d2a7",
    granappproduction: "6079c2e2ead2430bf34877f8",
  },
  "g-integrations": {
    granappdevelopment: "5f21722deeb6640bce302828",
    granapptest: "5f21722eea13fb0b5878e00c",
    granappproduction: "5f21722f5d898d0b2a1ff8e7",
  },
  g_insights: {
    granappdevelopment: "5f6d00cf7fac960b2083851b",
    granapptest: "5f6d00d162ab4e0ac2080aa0",
    granappproduction: "5f6d00d1db33a20ba1a386b0",
  },
  agronomy: {
    granappdevelopment: "5f6cfc384495ad0b48c0b79a",
    granapptest: "5f6cfc3adb33a20ba1a3861a",
    granappproduction: "5f6cfc3af6be160b712f5043",
  },
  activities: {
    granappdevelopment: "605223bf28a3970eacbff353",
    granapptest: "6052241fa58b110f201e2381",
    granappproduction: "6052247787762a0e27b7ea14",
  },
  "document-service": {
    granappdevelopment: "60e4ae5b680b360cab47e06f",
    granapptest: "60e4aebed4734b0c78a097ad",
    granappproduction: "60e4aee6e2c813250ec953e5",
  },
  analyze: {
    granappdevelopment: "650df28fa2b9641271d9f7d5",
    granapptest: "650df2bb93b26f1589fbfcbe",
    granappproduction: "650df3479633631a9afee3e3",
  },
};

export type FeatureFlagsFetcherParams = {
  // IMPORTANT: These must be JSON stringify-able values.
  // Do not allow `undefined` for any of these values.

  appName: AppName;
  containerName: string;
  granularRuntimeEnvName: GranularRuntimeEnvName;
  country: string;
  userId: string | null;
  operationId: string | null;
};
export type FeatureFlagsQueryKey = FetcherQueryKey<FeatureFlagsFetcherParams>;

export const featureFlagsQueryKeyPrefix = "[@fabric/core][featureFlags]";

/**
 * Computes the featureFlagsQueryKey from the given params.
 */
export const computeFeatureFlagsQueryKey = (
  params: FeatureFlagsFetcherParams,
): FeatureFlagsQueryKey => [featureFlagsQueryKeyPrefix, params];

// TODO: This likely will need to have some sort of LRU cache clearing mechanism
// because it is possible for someone to be switching through a lot
// of operations and thus adding a lot of entries to this object.
// OR another solution is to remove data relating to all other operations
// when adding data relating to an operation.
const launchDarklyInstances: Record<string, LaunchDarkly.LDClient | undefined> =
  {};

export const getLaunchDarklyInstance = (
  fetcherParams: FeatureFlagsFetcherParams,
) => {
  const fetcherParamsStringified = jsonStableStringify(fetcherParams);
  return launchDarklyInstances[fetcherParamsStringified];
};

export const saveLaunchDarklyInstance = (
  fetcherParams: FeatureFlagsFetcherParams,
  instance: LaunchDarkly.LDClient,
) => {
  const fetcherParamsStringified = jsonStableStringify(fetcherParams);
  launchDarklyInstances[fetcherParamsStringified] = instance;
};

export const deleteLaunchDarklyInstance = async (
  fetcherParams: FeatureFlagsFetcherParams,
) => {
  const fetcherParamsStringified = jsonStableStringify(fetcherParams);
  const existingInstance = launchDarklyInstances[fetcherParamsStringified];
  if (existingInstance) {
    await existingInstance.close();
  }
  delete launchDarklyInstances[fetcherParamsStringified];
};

export const deleteAllLaunchDarklyInstances = async () => {
  const promises = Object.keys(launchDarklyInstances).map(async (key) => {
    const existingInstance = launchDarklyInstances[key];
    if (existingInstance) {
      await existingInstance.close();
    }
    delete launchDarklyInstances[key];
  });
  await Promise.all(promises);
};

/**
 * Fetches the feature flags using the "launchdarkly-js-client-sdk".
 */
export const featureFlagsFetcher: QueryFunction<
  FeatureFlagsNetworkResponse,
  FeatureFlagsFetcherParams
> = async (context) => {
  const fetcherParams = context.queryKey[1];
  const existingInstance = getLaunchDarklyInstance(fetcherParams);
  if (existingInstance) {
    // We have already fetched flags. Just return what we have (error or results)
    try {
      await existingInstance.waitForInitialization();
      return existingInstance.allFlags();
    } catch (error) {
      await existingInstance.close();
      await deleteLaunchDarklyInstance(fetcherParams);
      throw error;
    }
  } else {
    // We need to fetch the flags and return the result.
    const {
      appName,
      containerName,
      country,
      granularRuntimeEnvName,
      userId,
      operationId,
    } = context.queryKey[1];
    const envKey = LAUNCH_DARKLY_KEYS[appName][granularRuntimeEnvName];
    const key = userId;
    const user = {
      ...(key && { key }),
      ...(!key && { anonymous: true }),
      country,
      custom: {
        application_name: containerName,
        ...(operationId && {
          operation_id: operationId,
        }),
      },
    };
    console.debug(
      `[@fabric/core][featureFlagsFetcher] Initializing LaunchDarkly instance for "${appName}"`,
      user,
    );
    const newInstance = LaunchDarkly.initialize(envKey, user);
    try {
      await newInstance.waitForInitialization();
    } catch (error) {
      await newInstance.close();
      throw error;
    }
    saveLaunchDarklyInstance(fetcherParams, newInstance);
    return newInstance.allFlags();
  }
};

/**
 * Compute the featureFlags derived data.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 */
export const computeFeatureFlagsAsBooleansByKey = (
  featureFlagsResponse: FeatureFlagsNetworkResponse,
): Record<string, boolean | undefined> => {
  return mapValues(featureFlagsResponse, (x) => Boolean(x));
};

/**
 * Compute the data envelope from the response.
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * This memoization is cleaned up by useCleanupMemoization.
 */
export const computeDataEnvelopeFromResponse = memoize(
  (
    featureFlagsResponse: FeatureFlagsNetworkResponse,
  ): FeatureFlagsDataEnvelope => {
    // Compute the derived data for the data envelope.
    const featureFlagsAsBooleansByKey =
      computeFeatureFlagsAsBooleansByKey(featureFlagsResponse);
    const dataEnvelope = { featureFlagsAsBooleansByKey };
    return dataEnvelope;
  },
  {
    profileName: "[featureFlags.query.helpers] computeDataEnvelopeFromResponse",
  },
);

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in useFeatureFlagsQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<FeatureFlagsNetworkResponse, Error>,
): UseQueryResult<FeatureFlagsDataEnvelope, Error> => {
  const dataEnvelope = resultOfUseQuery.data
    ? computeDataEnvelopeFromResponse(resultOfUseQuery.data)
    : undefined;

  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<FeatureFlagsDataEnvelope, Error>;
};

export const computeIsQueryEnabled = (
  fetcherParams: FeatureFlagsFetcherParams,
  {
    ignoreUserId,
    ignoreOperationId,
    forceDisable,
  }: {
    ignoreUserId: boolean;
    ignoreOperationId: boolean;
    forceDisable?: boolean;
  },
) => {
  const existingInstance = getLaunchDarklyInstance(fetcherParams);
  if (existingInstance) {
    return false;
  }

  const fetcherParamsToCheck: Partial<FeatureFlagsFetcherParams> = {
    ...fetcherParams,
  };

  if (ignoreUserId) {
    delete fetcherParamsToCheck.userId;
  }
  if (ignoreOperationId) {
    delete fetcherParamsToCheck.operationId;
  }

  return computeIsQueryEnabledFromFetcherParams(fetcherParamsToCheck, {
    forceDisable,
  });
};

/**
 * This hook contains the logic to run in useFeatureFlagsQuery
 * to cleanup any global memoization on query removal.
 *
 * This hook is only meant for use in useFeatureFlagsQuery.
 */
export const useCleanupMemoization = (queryKey: FeatureFlagsQueryKey) => {
  useEffect(() => {
    registerOnQueryRemoveCallback({
      queryKey,
      onRemove: (data: FeatureFlagsNetworkResponse) => {
        computeDataEnvelopeFromResponse.delete(data);
      },
    });
  }, [queryKey]);
};

export const getContainerNameFromUrl = (): string =>
  takeRight(window.location.hostname.split("."), 3).join(".");
